import { useEffect, useState } from "react";
import { Face } from "../components/Face";
import { FitText } from "../components/FitText";
import { MascotSays } from "../components/Mascot";
import { ACCENTS, DEFAULT_LETTERING, DEFAULT_LOOK, LETTERINGS, PALETTES, useLook } from "../lib/look";
import { lineFor, poseFor } from "../lib/mascot";
import { useTournament, type Screen } from "../lib/useTournament";
import { DEFAULT_TUNING, filterOf, loadTuning, lookEvent, saveTuning, type Look, type Tuning } from "./projector/calibration";
import "@fontsource/barlow-condensed/latin-600.css";
import "../far.css";
import "./Projector.css";
import "./Calibration.css";

/** Two sample names a court: a short one and the longest on the list. */
const SAMPLE = [
  ["Abhi", "Thelaksan"],
  ["Pooja S", "Ani"],
  ["Khushal", "Sam"],
  ["Deepali", "Raul"],
] as const;

/**
 * Where the wall decides: the real pieces of the projector in the chosen look, a button for each
 * palette, accent and lettering (saved for every screen), and this browser's brightness and contrast.
 */
export function CalibrationView({ screen }: { screen: Screen }) {
  const t = useTournament(screen);
  const [tuning, setTuning] = useState<Tuning>(loadTuning);
  useEffect(() => saveTuning(tuning), [tuning]);
  const set = (patch: Partial<Tuning>) => setTuning((old) => ({ ...old, ...patch }));

  // A pick shows at once and is sent to everyone; when the saved look changes, from here or from
  // anywhere else, the screen follows it.
  const saved = t.state?.config.look ?? DEFAULT_LOOK;
  const [picked, setPicked] = useState<Look | null>(null);
  useEffect(() => setPicked(null), [saved.palette, saved.accent, saved.lettering]);
  const look = picked ?? saved;
  useLook(look);
  const pick = (patch: Partial<Look>) => {
    const next = { ...look, ...patch };
    setPicked(next);
    t.send(lookEvent(next));
  };

  const back = `/p/${screen.token}${location.search}`;

  return (
    <div className="far cal" style={{ filter: filterOf(tuning) }}>
      <header className="proj-top">
        <div className="proj-title">
          <h1 className="wall-display">Round 7</h1>
          <p className="proj-sub">Now playing</p>
          <p className="proj-sub">Round 7 of about 22 · last round 17:35</p>
        </div>
        <div className="proj-count wall-display">10:00</div>
        <MascotSays className="proj-host" pose={poseFor("idle")} line={lineFor("idle", 0)} />
      </header>

      <main className="cal-courts">
        {SAMPLE.map((names, i) => (
          <section key={i} className="card" data-court={i + 1} style={{ background: `var(--bd-court-${i + 1})` }}>
            {i === 1 ? <div className="card-tag">Mini game</div> : null}
            <div className="card-head">
              <div className="card-no wall-display">
                <span>Court</span>
                <b>{i + 1}</b>
              </div>
            </div>
            <div className="card-pair">
              {names.map((name) => (
                <div key={name} className="who">
                  <Face name={name} />
                  <FitText className="who-name wall-display">{name}</FitText>
                </div>
              ))}
            </div>
          </section>
        ))}
      </main>

      <section className="cal-board">
        <ol className="board-col">
          <li className="board-head">
            <span>#</span>
            <span>Name</span>
            <span>Games</span>
            <span>Avg</span>
          </li>
          <li className="board-row">
            <span className="board-rank">1</span>
            <FitText className="board-name">Neehal</FitText>
            <span className="board-games">6</span>
            <span className="board-avg">17.5</span>
          </li>
          <li className="board-row ineligible">
            <span className="board-rank">24</span>
            <FitText className="board-name">Vinisha</FitText>
            <span className="board-games">2</span>
            <span className="board-avg">11.0</span>
          </li>
        </ol>
        <ol className="board-col board-fun">
          <li className="board-head">
            <span>#</span>
            <span>Fun board</span>
            <span />
            <span>Pts</span>
          </li>
          <li className="board-row">
            <span className="board-rank">1</span>
            <FitText className="board-name">Nayan</FitText>
            <span />
            <span className="board-avg">12</span>
          </li>
        </ol>
        <div className="cal-strip">
          <p className="strip-nag">
            <FitText className="wall-display">Court 2: a late score looks like this</FitText>
          </p>
          <p className="cal-next">
            {[1, 2, 3, 4].map((n) => (
              <span key={n} className="next-no" style={{ background: `var(--bd-court-${n})` }}>
                {n}
              </span>
            ))}
            <span>Up next</span>
          </p>
        </div>
      </section>

      <section className="cal-controls">
        <div className="cal-group" role="group" aria-label="Palette">
          <span className="cal-label">Ground</span>
          {PALETTES.map((p) => (
            <button key={p.id} type="button" className={look.palette === p.id ? "cal-btn on" : "cal-btn"} aria-pressed={look.palette === p.id} onClick={() => pick({ palette: p.id })}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="cal-group" role="group" aria-label="Accent">
          <span className="cal-label">Accent</span>
          {ACCENTS.map((a) => (
            <button key={a.id} type="button" className={look.accent === a.id ? "cal-btn on" : "cal-btn"} aria-pressed={look.accent === a.id} onClick={() => pick({ accent: a.id })}>
              {a.label}
            </button>
          ))}
        </div>
        <div className="cal-group" role="group" aria-label="Lettering">
          <span className="cal-label">Lettering</span>
          {LETTERINGS.map((l) => {
            const on = (look.lettering ?? DEFAULT_LETTERING) === l.id;
            return (
              <button key={l.id} type="button" className={on ? "cal-btn on" : "cal-btn"} aria-pressed={on} onClick={() => pick({ lettering: l.id })}>
                {l.label}
              </button>
            );
          })}
        </div>
        <label className="cal-slider">
          <span>Brightness {tuning.brightness}%</span>
          <input type="range" min={50} max={150} step={5} value={tuning.brightness} onChange={(e) => set({ brightness: Number(e.target.value) })} />
        </label>
        <label className="cal-slider">
          <span>Contrast {tuning.contrast}%</span>
          <input type="range" min={50} max={150} step={5} value={tuning.contrast} onChange={(e) => set({ contrast: Number(e.target.value) })} />
        </label>
        <button type="button" className="cal-btn" onClick={() => setTuning(DEFAULT_TUNING)}>
          Reset
        </button>
        <a className="cal-btn cal-back" href={back}>
          Back to the projector
        </a>
      </section>
    </div>
  );
}
